<template>
<div class="chapter">
<div class="inner_page">
<div>
<a id="p1"></a><h1 class="chapter-title"><span><a href="#/toc#toc-prologue">PROLOGUE</a></span></h1>
<p class="noindent">Edward Popple saw the axe fall but not the axe-head find its object. The block was low and the platform was hung high with
         black drapes to hide the meeting of metal and flesh from the pressing crowd. In the instant at which the king’s head was severed
         from his body a visceral groan rose from the assembly, as if the people feared that their world would end too. Despite himself,
         a sound gurgled in Popple’s throat. The bearded fellow in front of him, hat in hand, bowed his head in respect, and the jolly
         boy on top of the wall made a sign of the cross, not knowing how else to proclaim that he was alive on the blackest day of
         the creation since the crucifixion of Our Lord. A man and woman fainted in each other’s arms. Edward Popple gave only a sour
         smile. It was sheer chance that he should be in the street on this coldest London day of his memory.
      </p>
<p>The bearded fellow had asked his profession, and when Popple said he was a horticulturist, the man, who was dressed in an
         outlandish, old-fashioned style, asked then what he did there. Popple explained how he had come down from the north to deliver
         letters to his patron on this day. He did not add that he had been unable to find Lord Brougham in the chambers of Westminster
         Hall, or that a messenger had come to him with a laissez-passer to the yard outside the Banqueting House. It was a plea, Popple
         understood, from his Lord for a space to deny the act of <a id="p2"></a>blood. Lord Brougham did not want to be there, and Popple was standing in as his witness, craning to see the scaffold, obliged
         to his bearded companion – who confessed to being a seaman – for doffing his plumed hat in good time. Popple saw the king
         remove his cloak and his doublet to expose a white shirt. Then the king wrapped his cloak around himself again for a spell
         from the cold. As the royal face turned to the executioner, a smocked man in a grey wig and false beard, Popple was reminded
         of the time he had seen the king and his queen years earlier, with Viscount Falconbridge, as players in the masque of <em>Albion’s Triumphs</em> inside the selfsame Banqueting House, the king as the god Apollo exposing his flesh to public gaze with a red swathe of satin
         across his ivory chest, and a black lady masquer as Peace. Today he saw the king lift his hands and his eyes to a white dry
         silent sky. The king’s lips recited no more words, save for the mild order, as it was afterwards reported, for the headsman
         to stay for a sign. Then the king dropped down behind the black scene like a puppet.
      </p>
<p>His head alone reappeared for the spectators, dripping from the axeman’s hand. The crowd was sucked helplessly towards it,
         even as the double ranks of mounted horse broke forward, hooves striking stone, to drive off those who were determined to
         dip themselves in the king’s blood. In a matter of minutes the yard was cleared.
      </p>
<p>Popple found himself hurrying with the bearded seaman, whose feather waved in the air. The man gripped Popple’s shoulder by
         virtue of the bond they had entered into that day and from his dangling coat pocket he extracted a token he was willing to
         part with as a memento.
      </p>
<p>‘Look at it, sir. It is no ordinary stone. A fellow at the docks brought it to me from Tartary. Look carefully, sir.’</p>
<p>They stopped at the crossroad and his palm opened to reveal a smooth green stone with a black marking that had been picked
         out in relief.
      </p>
<a id="p3"></a>
<p>Bringing the stone close to his eye, Popple made out the black shape of a rose. He looked up in wonderment at the bearded
         man whose eyes, hidden under the brim of the outlandish hat, seemed to gaze from the furthest reaches of the orb.
      </p>
<p>‘The curio is jadestone. It grows warm to the touch.’</p>
<p>‘What do you want for it?’ asked Edward.</p>
<p>‘On this day? A half-sovereign to see me through.’</p>
<p>They parted with a handshake, commending each other to fare well through the troublous times. By now the troop horses were
         returning calmly down the empty street. The frozen sky shone white. Heaven had no tears for Charles.
      </p>
<p><em>Monarchy is dead. Long live the republic!</em></p>
<p>In his pocket Popple closed his fist around the warm stone.</p>
<p class="cotx">Popple stayed in London for some days, watching for the uncertain consequences of the execution, until he felt it safe to
         go. His innkeeper got him a copy of the secret book of the king’s image, <em>Eikon Basilike</em>, which he would give his son Henry to remember the day. Rosamund his daughter would have the curio stone. It occurred to
         him that he had nothing for his wife. He would buy her some black cloth.
      </p>
<p>On his journey north he found himself spreading the news. He disliked having to report, but could not deny, if he was asked,
         that he had been there, and when folk found out, they flocked to him, to look and touch as much as to hear. Then as quickly
         they shrunk away, as if he were infected with the horror he had seen. He soon learned to avoid talk.
      </p>
<p>He travelled over miry roads that were troubled by people on the move, men on the way to be soldiers or others escaping that
         duty, masterless types driven by <a id="p4"></a>rumour and fancy, broken families, or souls just making for anywhere they might be safe and free. The citizens of broken England
         roamed ground that was slippery with blood and as cold as death. Muskets and hayforks were what people heeded when a shape
         approached through the drizzle. Passes, uniforms and credentials counted for little. He travelled in a rough woollen cloak
         with high collar and cap, and an unsheathed dagger on his belt. He was relieved to reach safe home.
      </p>
<p>His family was in the chapel with Lady Brougham. The news had gone before him to Brougham House and Lady Brougham already
         had by memory the last words of Charles, the unbreakable diamond.
      </p>
<p>‘I go from a corruptible to an incorruptible crown,’ she recited with tearful admiration, hopeful of doing the same herself
         before long.
      </p>
<p>Delia Popple embraced Edward, fixing him with her shrewd eyes to see how his presence at the event had changed him and might
         be turned to account. Rosamund and Henry saw their father in a new light, aware suddenly what enormities of experience his
         paucity of speech concealed.
      </p>
<p>Lady Brougham insisted on a grace from Popple at supper.</p>
<p>‘We give thanks for deliverance, sustenance, and the grace of fortitude. We beseech Thy guidance in darkness and Thy helping
         power against destruction, that we may once again enjoy Thy love in light.’
      </p>
<p>The women sighed. Henry Popple smacked his lips.</p>
<p>The servant carved the cut of venison. A draught blew through the hall when the groom opened the door to bring a jug of water.
         On Lady Brougham’s instructions they drank only water in the house. They were in mourning, and in transition to the new puritan
         ways.
      </p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// prologue.vue?4db130b8